import { Component, OnInit, ViewChild, inject, signal, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { take } from 'rxjs/operators';
import { EuiAppShellService, EuiGrowlService, UserService } from '@eui/core';
import {
    FormBuilder,
    FormControl,
    FormGroup,
    ReactiveFormsModule,
    ValidatorFn,
    Validators,
} from '@angular/forms';
import { EUI_SELECT } from '@eui/components/eui-select';
import { SharedModule } from '@shared/shared.module';
import { EUI_FILE_UPLOAD } from '@eui/components/eui-file-upload';
import { EUI_DATEPICKER } from '@eui/components/eui-datepicker';
import { EuiDialogComponent } from '@eui/components/eui-dialog';
import { SUPPORT_TOPICS } from './support-topics.data';
import { SupportApiService, SupportTicketPayload } from './services/support-api.service';
import { TextualCaptchaComponent } from './captcha/textual/textual.captcha.component';
import {
    Category,
    CategoryInput,
    CategoryLink,
    CategoryWarning,
    SelectOption,
    SupportUser,
    Topic,
} from './models/support.interface';

@Component({
    selector: 'app-support',
    templateUrl: './support.component.html',
    styleUrl: './support.component.scss',
    imports: [
        TextualCaptchaComponent,
        TranslateModule,
        ...EUI_SELECT,
        ...EUI_FILE_UPLOAD,
        ...EUI_DATEPICKER,
        ReactiveFormsModule,
        SharedModule,
    ],
    standalone: true,
})
export class SupportComponent implements OnInit {
    private asService = inject(EuiAppShellService);
    private fb = inject(FormBuilder);
    private userService = inject(UserService);
    private route = inject(ActivatedRoute);
    protected translate = inject(TranslateService);
    private supportApiService = inject(SupportApiService);
    private growlService = inject(EuiGrowlService);
    private destroyRef = inject(DestroyRef);
    public form: FormGroup;
    public topics = signal<SelectOption[]>([]);
    public categories = signal<SelectOption[]>([]);
    public selectedInputs = signal<CategoryInput[]>([]);

    private topicsData = signal<Topic[]>([]);
    private selectedTopic = signal<string | null>(null);
    private selectedCategory = signal<string | null>(null);

    public showAffectedEmail = false;
    public user: SupportUser | null = null;

    @ViewChild('topicWarningDialog') topicWarningDialog!: EuiDialogComponent;
    @ViewChild(TextualCaptchaComponent)
    captchaComponent?: TextualCaptchaComponent;

    public topicWarningMessage = signal<CategoryWarning | null>(null);
    public selectedCategoryLinks = signal<CategoryLink[]>([]);

    public allowedFileTypes =
        '.bmp,.csv,.doc,.docx,.htm,.html,.jpeg,.jpg,.pdf,.png,.ppt,.pptx,.tif,.tiff,.txt,.xls,.xlsx,.xml,.zip';

    ngOnInit() {
        this.form = this.fb.group({
            firstName: ['', Validators.required],
            lastName: ['', Validators.required],
            email: ['', [Validators.required, Validators.email]],
            requestSomeoneElse: [false],
            affectedEmail: new FormControl({ value: '', disabled: true }),
            topic: ['', Validators.required],
            category: new FormControl('', Validators.required),
            timestamp: [''],
            summary: [''],
            network: [''],
        });
        this.initializeTopicsData();
        this.setupFormValueChanges();
        this.updateAffectedEmailState(false);
        this.populateCallerFromUserInfos();
        this.applyQueryParams();
    }

    private populateCallerFromUserInfos(): void {
        this.userService
            .getState()
            .pipe(take(1))
            .subscribe((user: SupportUser) => {
                this.user = user;
                const firstName = (user?.firstName || '').trim();
                const lastName = (user?.lastName || '').trim();
                const email = (user?.email || '').trim();
                const hasUser = !!(firstName || lastName || user?.fullName);
                const hasEmail = !!email;

                this.form.patchValue({ firstName, lastName, email }, { emitEvent: false });

                const {
                    firstName: fNameCtrl,
                    lastName: lNameCtrl,
                    email: emailCtrl,
                } = this.form.controls;

                if (hasUser) {
                    fNameCtrl.disable({ emitEvent: false });
                    lNameCtrl.disable({ emitEvent: false });
                    this.showAffectedEmail = true;
                } else {
                    fNameCtrl.enable({ emitEvent: false });
                    lNameCtrl.enable({ emitEvent: false });
                    this.showAffectedEmail = false;
                }

                if (hasEmail) {
                    emailCtrl.disable({ emitEvent: false });
                } else {
                    emailCtrl.enable({ emitEvent: false });
                }
            });
    }

    private setupFormValueChanges(): void {
        // takeUntilDestroyed prevents memory leaks from continuous RxJS streams
        this.form.controls.requestSomeoneElse.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                this.updateAffectedEmailState(!!value);
            });

        this.form.controls.topic.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((value) => {
                if (value) {
                    this.onTopicsChanged(value);
                } else {
                    this.categories.set([]);
                    this.selectedInputs.set([]);
                    this.clearDynamicControls();
                    this.form.controls.category.reset('');
                }
            });

        this.form.controls.category.valueChanges
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe((category) => {
                if (category) {
                    this.onCategoriesChanged(category.value.id);
                } else {
                    this.selectedInputs.set([]);
                    this.clearDynamicControls();
                }
            });
    }

    private updateAffectedEmailState(isRequestedForSomeoneElse: boolean): void {
        const affectedEmailControl = this.form.controls.affectedEmail;

        if (isRequestedForSomeoneElse) {
            affectedEmailControl.enable({ emitEvent: false });
            affectedEmailControl.setValidators([Validators.required, Validators.email]);
        } else {
            affectedEmailControl.clearValidators();
            affectedEmailControl.reset({ value: '', disabled: true }, { emitEvent: false });
        }

        affectedEmailControl.updateValueAndValidity({ emitEvent: false });
    }

    isAffectedEmailRequired(): boolean {
        return !!this.form.controls.requestSomeoneElse.value;
    }

    private applyQueryParams(): void {
        const params = this.route.snapshot.queryParamMap;

        if (params.has('topic')) this.form.controls.topic?.setValue(params.get('topic'));

        if (params.has('category')) {
            const selectedCategory = this.categories().find(
                (option) => (option.value as { id?: string })?.id === params.get('category')
            );
            if (selectedCategory) {
                this.form.controls.category.setValue(selectedCategory);
            }
        }

        if (params.has('timestamp'))
            this.form.controls.timestamp?.setValue(params.get('timestamp'), {
                emitEvent: false,
            });
        if (params.has('summary'))
            this.form.controls.summary?.setValue(params.get('summary'), {
                emitEvent: false,
            });
        if (params.has('network'))
            this.form.controls.network?.setValue(params.get('network'), {
                emitEvent: false,
            });
    }

    private initializeTopicsData(): void {
        const normalizedTopics = this.normalizeSupportTopicsKeys(SUPPORT_TOPICS);
        this.topicsData.set(normalizedTopics);
        this.topics.set(
            normalizedTopics.map((t) => ({
                value: t.id,
                label: t.labelKey ?? t.label,
            }))
        );
    }

    private prefixSupportKey(key?: string): string | undefined {
        return key?.startsWith('support.') ? `page.${key}` : key;
    }

    private normalizeSupportTopicsKeys(topics: Topic[]): Topic[] {
        return topics.map((topic) => ({
            ...topic,
            labelKey: this.prefixSupportKey(topic.labelKey),
            categories: topic.categories.map((category: Category) => ({
                ...category,
                labelKey: this.prefixSupportKey(category.labelKey),
                warning: category.warning
                    ? {
                          ...category.warning,
                          titleKey: this.prefixSupportKey(category.warning.titleKey) as string,
                          contentKey: this.prefixSupportKey(category.warning.contentKey) as string,
                      }
                    : undefined,
                links: category.links?.map((link) => ({
                    ...link,
                    labelKey: this.prefixSupportKey(link.labelKey) as string,
                })),
                inputs: category.inputs.map((input) => ({
                    ...input,
                    options: input.options?.map((option) => ({
                        ...option,
                        labelKey: this.prefixSupportKey(option.labelKey),
                    })),
                })),
            })),
        }));
    }

    onFileSelected(controlName: string, event: Event): void {
        const input = event.target as HTMLInputElement;
        const file = input.files?.[0] ?? null;
        const control = this.form.get(controlName);
        if (control) {
            control.setValue(file);
            control.markAsDirty();
            control.markAsTouched();
            control.updateValueAndValidity();
        }
    }

    markControlTouched(controlName: string): void {
        const control = this.form.get(controlName);
        control?.markAsTouched();
        control?.markAsDirty();
        control?.updateValueAndValidity({ emitEvent: false });
    }

    onTopicsChanged(value: string): void {
        this.selectedTopic.set(value);
        this.selectedCategory.set(null);
        this.form.controls.category?.reset('');
        this.selectedInputs.set([]);
        this.clearDynamicControls();

        const topic = this.topicsData().find((t) => t.id === value);
        if (topic) {
            this.categories.set(
                topic.categories.map((c) => ({
                    value: c,
                    label: c.labelKey ?? c.label,
                }))
            );
        } else {
            this.categories.set([]);
        }
    }

    onCategoriesChanged(value: string): void {
        this.selectedCategory.set(value);
        const topic = this.topicsData().find((t) => t.id === this.selectedTopic());

        if (topic) {
            const category = topic.categories.find((c) => c.id === value);
            if (category) {
                if (category.showWarning && category.warning && value) {
                    this.topicWarningMessage.set(category.warning);
                    this.topicWarningDialog?.openDialog();
                }
                this.selectedCategoryLinks.set(category.links ?? []);
                this.updateFormControls(category.inputs);
                this.selectedInputs.set(category.inputs);
            } else {
                this.selectedCategoryLinks.set([]);
                this.clearDynamicControls();
                this.selectedInputs.set(category?.inputs ?? []);
            }
        }
    }

    private updateFormControls(inputs: CategoryInput[]): void {
        this.clearDynamicControls();

        inputs.forEach((input) => {
            const validators: ValidatorFn[] = input.required ? [Validators.required] : [];
            this.form.addControl(
                input.name,
                new FormControl(input.type === 'file' ? null : '', validators)
            );
        });
    }

    isControlInvalid(controlName: string): boolean {
        const control = this.form.get(controlName);
        return !!control && control.invalid && (control.touched || control.dirty);
    }

    hasCaptchaAnswer(): boolean {
        return !!this.captchaComponent?.answer;
    }

    isFormValidWithoutCaptcha(): boolean {
        return this.form.valid;
    }

    getControlError(controlName: string): string {
        const control = this.form.get(controlName);

        if (!control?.errors) return '';
        if (control.errors['required']) return this.translate.instant('form.required');
        if (control.errors['email']) return this.translate.instant('form.invalid.email');

        return this.translate.instant('form.invalid.format');
    }

    private clearDynamicControls(): void {
        const coreControls = [
            'firstName',
            'lastName',
            'email',
            'requestSomeoneElse',
            'affectedEmail',
            'topic',
            'category',
            'timestamp',
            'summary',
            'network',
        ];

        Object.keys(this.form.controls).forEach((key) => {
            if (!coreControls.includes(key)) {
                this.form.removeControl(key);
            }
        });
    }

    submit(): void {
        this.form.markAllAsTouched();

        if (
            !this.isFormValidWithoutCaptcha() ||
            !this.hasCaptchaAnswer() ||
            !this.captchaComponent
        ) {
            return;
        }

        this.asService.isBlockDocumentActive = true;

        const raw = this.form.getRawValue();
        const dynamicFields = this.getDynamicFieldValues(raw);
        const detailedDescription = (dynamicFields['description'] as string) ?? '';

        const payload: SupportTicketPayload = {
            files: dynamicFields['file'] as File | File[],
            payloadJson: {
                incidentRequest: {
                    incident: {
                        business_service: 'SANTE Applications',
                        service_offering: 'SANTE IMSOC SERVICE',
                        assignment_group: 'SANTE RASFF Support',
                        u_external_first_name: raw.firstName,
                        u_external_last_name: raw.lastName,
                        u_external_email: raw.email,
                        category: raw.category.value.category,
                        impact: raw.category.value.impact,
                        urgency: raw.category.value.urgency,
                        short_description: raw.category.value.short_description,
                        caller_id:
                            this.user?.userId === 'annonymous'
                                ? 'guest'
                                : (this.user?.userId || '').split('-')[0],
                        description: detailedDescription,
                    },
                },
            },
        };

        this.captchaComponent.onValidate(
            () => this.submitTicket(payload, raw),
            () => {
                this.asService.isBlockDocumentActive = false;
            }
        );
    }

    private submitTicket(payload: SupportTicketPayload, raw): void {
        this.supportApiService
            .submitTicket(payload)
            .pipe(take(1))
            .subscribe({
                next: () => {
                    this.asService.isBlockDocumentActive = false;
                    this.growlService.growl({
                        severity: 'success',
                        summary: `${this.translate.instant('page.support.send.success')} !`,
                    });
                    this.form.reset({
                        firstName: raw.firstName,
                        lastName: raw.lastName,
                        email: raw.email,
                    });
                },
                error: () => {
                    this.asService.isBlockDocumentActive = false;
                },
            });
    }

    private getDynamicFieldValues(raw: Record<string, unknown>): Record<string, unknown> {
        const files: File[] = [];
        const description = this.selectedInputs()
            .filter((input) => {
                if (input.type === 'file') {
                    const value = raw[input.name];
                    if (Array.isArray(value)) {
                        files.push(...value.filter((item): item is File => item instanceof File));
                    } else if (value instanceof File) {
                        files.push(value);
                    }
                    return false;
                }
                return true;
            })
            .map((input) =>
                String(raw[input.name] ? `${input.name}: ${raw[input.name]}` : '').trim()
            )
            .filter((value) => value.length > 0)
            .join(', ');

        const affectedEmail = String(raw['affectedEmail'] ?? '').trim();
        const fullDescription = [description, affectedEmail].filter((v) => v.length > 0).join(', ');

        return {
            ...(files.length > 0 ? { file: files } : {}),
            description: fullDescription,
        };
    }
}
